/*eslint-disable*/
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableColumn } from 'react-data-table-component';

import Arrow from 'src/assets/arrow-left-black.svg';

import { DatePicker, RegularButton, SearchInput } from 'pg-ui-library';

import useDebounce from 'src/hooks/useDebounce';
import { SettingsButton } from 'src/components';

import ApprovalsService, { EmployeeReviewType } from 'src/services/approvals.service';
import {
  Container,
  ItemContainer,
  ArrowBack,
  NavTextLink,
  ItemContainerHeader,
  PageTitle,
  HeaderLeftItems,
  HeaderRightItems,
  HeaderLeftFirstItem,
  StyledErrorWrap,
} from './styles';
import { EmployeeReviewListTable } from './employee-review-table';
import {
  EmployeeReviewChangesContextProvider,
  useReviewModalIsOpen,
  useSetCurrentEmployeeReviewType,
  useSetReviewModalIsOpen,
} from './employee-review-changes.context';
import { BenefitReviewModal } from './benefit-review-modal';
import { NewFiltersReviewModal } from './new-filters-review';
import { DependentsApprovalModal } from 'src/components/dependents-approval-modal';
import { formatDateConverter } from 'src/utils/formatDateConvert';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import CompanyService from 'src/services/company.services';

type ParamTypes = { startDate: Date; endDate: Date; searchTerm: string };

const EmployeeReviewChanges: React.FC = () => {
  const employeeReviewTableColumns = [
    {
      name: 'Employee Name',
      selector: (row: EmployeeReviewType) => row.employeeName,
      width: '320px',
      sortField: 'employeeName',
      omit: false,
      sortable: true,
      reorder: true,
      wrap: true,
    },
    {
      name: 'Emp.ID',
      selector: (row: EmployeeReviewType) => (row.personnelNumber ? row.personnelNumber : row.createdBy),
      width: '140px',
      sortField: 'employeeId',
      omit: false,
      sortable: true,
      reorder: true,
      wrap: true,
    },
    {
      name: 'Company ID',
      selector: (row: EmployeeReviewType) => row.employeeClass,
      width: '160px',
      sortField: 'employeeClass',
      omit: false,
      sortable: true,
      reorder: true,
      wrap: true,
    },
    {
      name: 'Employment Type',
      selector: (row: EmployeeReviewType) => row.positionType,
      width: '190px',
      sortField: 'positionType',
      omit: false,
      sortable: true,
      reorder: true,
      wrap: true,
    },
    {
      name: 'Submitted Date',
      selector: (row: EmployeeReviewType) =>
        row.submittedDate ? row.submittedDate : formatDateConverter(row.createdAt as unknown as ''),
      width: '250px',
      sortField: 'submittedDate',
      omit: false,
      sortable: true,
      reorder: true,
      wrap: true,
    },
    {
      name: 'Email',
      selector: (row: EmployeeReviewType) => {
        if (!row.email) return '';
        const email = String(row.email);
        if (email.length >= 4) {
          return `*****${email.slice(4)}`;
        }
        return email;
      },
      width: '300px',
      sortField: 'primaryContactEmail',
      omit: false,
      sortable: true,
      reorder: true,
    },
    {
      name: 'Change',
      selector: (row: EmployeeReviewType) => {
        if (row.change) {
          switch (row.change) {
            case 'new_hire':
              return 'New Hire';
            case 'DEPENDENT':
              return 'Dependent';
            case 'qle':
              return 'QLE';
            default:
              return row.change;
          }
        } else {
          switch (row.requestEventType) {
            case 'new_hire':
              return 'New Hire';
            case 'DEPENDENT':
              return 'Dependent';
            case 'qle':
              return 'QLE';
            default:
              return row.requestEventType;
          }
        }
      },
      width: '160px',
      sortField: 'requestEventType',
      omit: false,
      sortable: true,
      reorder: true,
      wrap: true,
    },
    {
      name: 'Status',
      selector: (row: EmployeeReviewType) => {
        if (row.requestEventType === 'CONTACT' || row.requestEventType === 'DEPENDENT') {
          switch (row.requestStatus) {
            case 'PENDING':
              return 'To Approve';
            case 'APPROVED':
              return 'Approved';
            case 'DECLINED':
              return 'Declined';
            default:
              return row.requestStatus || '';
          }
        } else {
          switch (row.approvalStatus) {
            case 'to_approve':
              return 'To Approve';
            case 'pending':
              return 'Pending';
            case 'approved':
              return 'Approved';
            case 'declined':
              return 'Declined';
            default:
              return row.approvalStatus || '';
          }
        }
      },
    },
    {
      cell: (row: EmployeeReviewType) => reviewButtonWrapper(row),
      allowOverflow: true,
      button: true,
    },
  ];
  const { t } = useTranslation();
  const reviewIsOpen = useReviewModalIsOpen();
  const [newFilterIsOpen, useNewFilterModalIsOpen] = useState<boolean>(false);
  const [isDependentOpen, setIsDependentOpen] = useState<boolean>(false);
  const [selectedDependent, setSelectedDependent] = useState<EmployeeReviewType>();
  const [columns, setColumns] = useState<TableColumn<EmployeeReviewType>[]>(
    employeeReviewTableColumns as unknown as []
  );

  const startDate = DateTime.now().minus({ days: 14 }).toJSDate();

  const [startDateFilter, setStartDate] = useState<Date>(startDate);
  const [endDateFilter, setEndDateFilter] = useState<Date>();
  const [companyIdFilter, setCompanyIdFilter] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [employmentType, setEmploymentType] = useState<string>('');
  const [changeType, setChangeType] = useState<string>('');
  const [employmentStatus, setEmploymentStatus] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<'benefits' | 'contacts' | 'bank'>('benefits');

  const [filters, setFilters] = useState({
    startDate: startDate,
    endDate: new Date(),
    selectedCompany: '',
    status: '',
    employmentType: '',
    changeType: '',
    employmentStatus: '',
  });
  const [statusKey, setStatusKey] = useState<number>(0);

  const updateFilters = (
    newStartDate: Date,
    newEndDate: any,
    newCompanyId: any,
    newStatus: string,
    newEmploymentType: string,
    newChangeType: string,
    newemploymentStatus: string
  ) => {
    setFilters({
      startDate: newStartDate,
      endDate: newEndDate,
      selectedCompany: newCompanyId,
      status: newStatus,
      employmentType: newEmploymentType,
      changeType: newChangeType,
      employmentStatus: newemploymentStatus,
    });
    setStatusKey(statusKey + 1);
    setEmploymentType(newEmploymentType);
    setChangeType(newChangeType);
    setStatus(newStatus);
    setStartDate(newStartDate);
    setEndDateFilter(newEndDate);
    setCompanyIdFilter(newCompanyId);
    setEmploymentStatus(newemploymentStatus);
  };

  const reviewButtonWrapper = (row: EmployeeReviewType) => {
    const setIsOpen = useSetReviewModalIsOpen();
    const setCurrentData = useSetCurrentEmployeeReviewType();
    return (
      <RegularButton
        style={{
          minWidth: '100px',
          maxWidth: '100px',
          background:
            row.approvalStatus === 'approved' ||
            row.approvalStatus === 'declined' ||
            row.requestStatus === 'APPROVED' ||
            row.requestStatus === 'DECLINED'
              ? '#2e8b57'
              : '',
        }}
        onClick={() => {
          if (row.change) {
            setIsOpen(true);
          } else {
            setSelectedDependent(row);
            setIsDependentOpen(!isDependentOpen);
          }
          setCurrentData(row);
        }}
      >
        {row.approvalStatus === 'approved' ||
        row.approvalStatus === 'declined' ||
        row.requestStatus === 'APPROVED' ||
        row.requestStatus === 'DECLINED'
          ? 'View'
          : 'Pending'}
      </RegularButton>
    );
  };

  const currentDate = new Date();
  const schema = yup.object({
    startDate: yup.date().max(yup.ref('endDate'), 'Start date must be earlier than end date').required(),
    endDate: yup.date().min(yup.ref('startDate'), 'End date must be later than start date').required(),
  });

  const formik = useFormik<ParamTypes>({
    initialValues: {
      startDate: startDate,
      endDate: currentDate,
      searchTerm: '',
    },
    validationSchema: schema,
    onSubmit: values => {},
  });

  const { data: workflow } = useQuery({
    queryKey: ['users/workflow'],
    queryFn: () => CompanyService.getWorkflow(),
    enabled: true,
  });

  useEffect(() => {
    setSelectedTab(workflow?.data[0]);
  }, [workflow]);

  const debouncedSearchTerm = useDebounce(formik.values.searchTerm, 500);

  return (
    <Container>
      {reviewIsOpen && <BenefitReviewModal />}
      <NewFiltersReviewModal
        isOpen={newFilterIsOpen}
        setIsOpen={useNewFilterModalIsOpen}
        updateFilters={updateFilters}
      />
      <DependentsApprovalModal
        isOpen={isDependentOpen}
        setIsOpen={setIsDependentOpen}
        selectedDependent={selectedDependent}
      />
      <ItemContainerHeader>
        <HeaderLeftItems>
          <HeaderLeftFirstItem>
            <NavTextLink to="/employee-management">
              <ArrowBack src={Arrow} />
            </NavTextLink>
            <PageTitle>Workflow</PageTitle>
          </HeaderLeftFirstItem>
        </HeaderLeftItems>
        <HeaderRightItems>
          <div style={{ marginTop: '30px' }}>
            <RegularButton
              onClick={() => {
                useNewFilterModalIsOpen(!newFilterIsOpen);
              }}
            >
              {t('employeeReviewChanges.newFilters')}
            </RegularButton>
          </div>

          <a
            href={ApprovalsService.downloadReviews(
              formik.values.startDate?.toISOString() ?? currentDate.toISOString(),
              formik.values.endDate?.toISOString() ?? currentDate.toISOString()
            )}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginTop: '30px' }}
          >
            <RegularButton>{t('employeeReviewChanges.downloadApprovedButton')}</RegularButton>
          </a>
          <div style={{ display: 'flex', flex: '1', marginTop: '30px', alignItems: 'center' }}>
            <SearchInput
              value={formik.values.searchTerm}
              placeholder="Search..."
              handleChange={event => {
                formik.setFieldValue('searchTerm', event.target.value);
              }}
            />

            <SettingsButton columns={columns} setColumns={setColumns} />
          </div>
        </HeaderRightItems>
      </ItemContainerHeader>
      <div style={{ display: 'flex', marginBottom: '16px', borderBottom: '1px grey solid' }}>
        {[
          { key: 'benefits', label: 'Benefits' },
          { key: 'contacts', label: 'Contacts' },
        ]
          .filter(list => workflow?.data?.includes(list.key))
          .map(tab => {
            const isSelected = selectedTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setSelectedTab(tab.key as 'benefits' | 'contacts' | 'bank')}
                style={{
                  color: isSelected ? 'white' : '#4948AC',
                  margin: '0.5em',
                  fontWeight: 500,
                  padding: '8px 20px',
                  maxHeight: '56px',
                  fontSize: '1.7rem',
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px',
                  fontFamily: 'Biotif, sans-serif',
                  textTransform: 'initial',
                  backgroundColor: isSelected ? '#4948AC' : 'white',
                  border: isSelected ? '#4948AC 1px solid' : 'grey 0.5px solid',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  marginBottom: '-1.2px',
                  marginLeft: '-2px',
                  width: '130px',
                }}
              >
                {tab.label}
              </button>
            );
          })}
      </div>
      <ItemContainer>
        <EmployeeReviewListTable
          isDependentOpen={isDependentOpen}
          key={statusKey}
          searchTerm={debouncedSearchTerm}
          columns={columns}
          startDate={startDateFilter?.toISOString() ?? currentDate.toISOString()}
          endDate={endDateFilter?.toISOString() ?? currentDate.toISOString()}
          status={status}
          employmentType={employmentType}
          changeType={changeType}
          employeeCompany={companyIdFilter}
          EmploymentStatus={employmentStatus}
          selectedTab={selectedTab}
        />
      </ItemContainer>
    </Container>
  );
};

const EmployeeReviewChangesStoreWrapper: React.FC = () => (
  <EmployeeReviewChangesContextProvider>
    <EmployeeReviewChanges />
  </EmployeeReviewChangesContextProvider>
);
export default EmployeeReviewChangesStoreWrapper;
